/* Copyright (c) 2021-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { FormProvider, useForm } from "react-hook-form";
import { When } from "react-if";
import { Button, FlexContainer, theme } from "@nordcloud/gnui";
import { BaselineTag } from "~/components";
import { ValueWrapper } from "~/components/Details/styles";
import { baselineTagToShow, getFlatPlanEntities } from "~/utils";
import { PlanErrors, PlanField } from "../../../constants";
import { usePlanWizard } from "../../../PlanProvider";
import { PlanEntity } from "../../../types";
import { useStepState } from "../../StepProvider";
import { ActionItemEdit } from "../ActionItemEdit";
import { PlanActionNameInput } from "./PlanActionNameInput";

type Props<T> = {
  items: T[];
  onDelete: (item: T) => void;
  batchId: string;
};

export function DraggableBatchActionsList<T extends PlanEntity>({
  items,
  onDelete,
  batchId,
}: Props<T>) {
  const { setPlanData, setSelectedEntity, planData } = usePlanWizard();
  const {
    stepState: { selectedEntity },
    updateStepState,
  } = useStepState();

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [hoveredActionId, setHoveredActionId] = useState<string | undefined>(
    undefined
  );

  const setPlanErrors = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: [
        ...new Set([...(prevPlanData[PlanField.ERRORS] ?? []), planError]),
      ],
    }));
  };

  const clearPlanError = (planError: PlanErrors) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.ERRORS]: prevPlanData[PlanField.ERRORS]?.filter(
        (errors) => errors !== planError
      ),
    }));
  };

  const handlePlanErrors = () => {
    if (Object.keys(errors).length > 0) {
      setPlanErrors(PlanErrors.PLAN_ACTION_NAMES);
    } else {
      clearPlanError(PlanErrors.PLAN_ACTION_NAMES);
    }
  };

  const formMethods = useForm();
  const {
    formState: { errors },
    clearErrors,
  } = formMethods;

  const flatEntities = useMemo(
    () =>
      getFlatPlanEntities(
        planData?.[PlanField.PLAN_SETTINGS]?.planEntities || []
      ),
    [planData]
  );

  return (
    <FlexContainer as="ul" direction="column" alignItems="stretch">
      {items?.map((i, index) => {
        const baselineTag = baselineTagToShow(i, flatEntities);
        const item = {
          ...i,
          icon: "dragAndDrop",
          name: (
            <FormProvider {...formMethods} key={i.name}>
              {editingItemId === i.listId ? (
                <PlanActionNameInput
                  key={i.name}
                  handlePlanErrors={handlePlanErrors}
                  item={i}
                  onEditComplete={() => setEditingItemId(null)}
                />
              ) : (
                <span
                  css={{
                    display: "flex",
                    paddingRight: theme.spacing.spacing07,
                  }}
                  onMouseOver={() => setHoveredActionId(i.listId)}
                  onMouseLeave={() => setHoveredActionId(undefined)}
                  onFocus={() => setHoveredActionId(i.listId)}
                  onBlur={() => setHoveredActionId(undefined)}
                >
                  <ValueWrapper
                    editable={hoveredActionId !== i.listId}
                    isEmpty={false}
                  >
                    {i.name}
                  </ValueWrapper>

                  <When condition={hoveredActionId === i.listId}>
                    <Button
                      aria-label="edit button"
                      icon="edit"
                      severity="low"
                      size="sm"
                      onClick={() => {
                        if (i.listId) {
                          setEditingItemId(i.listId);
                        }
                      }}
                      css={{ marginLeft: theme.spacing.spacing02 }}
                    />
                  </When>
                </span>
              )}
            </FormProvider>
          ),
        };

        return (
          <Draggable
            key={item.listId}
            draggableId={item.listId ?? ""}
            index={index}
          >
            {(provided) => {
              return (
                <ActionItemEdit
                  isCheckbox={false}
                  onMouseDown={() => {
                    setSelectedEntity?.(item.listId);
                    updateStepState({
                      selectedEntity: {
                        ...item,
                        name: item.name.props.children.props.defaultValue,
                        batchId: batchId,
                      },
                    });
                  }}
                  onDelete={() => {
                    clearErrors(item.listId);
                    handlePlanErrors();
                    onDelete(item);
                  }}
                  aria-label={item.name}
                  active={selectedEntity?.listId === item?.listId}
                  {...{ item }}
                  {...{ provided }}
                  noDesc
                  editName={item.name}
                  tag={
                    baselineTag ? <BaselineTag tag={baselineTag} /> : undefined
                  }
                />
              );
            }}
          </Draggable>
        );
      })}
    </FlexContainer>
  );
}
